import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { getFlattenedData } from "@shared/utils/data/data-utils";
import { useMemo, useRef } from "react";
import { isEventEligibleForUpsell } from "@app-contexts/public/shared/checkout/components/steps/add-ons/eventFilters";
import usePublicProductsService from "@app-contexts/public/features/products/data/services/usePublicProductsService";
import { useDonationsAddon } from "@shared/modules/events/components/donations/hooks/useDonationsAddon";
import { DataProvider } from "@shared/constants/common";
import { usePublicExchangeManager } from "@app-contexts/public/features/exchanges/hooks/usePublicExchangeManager";

/** Default sort order for product addons - used to ensure consistent React Query cache keys */
export const DEFAULT_PRODUCT_SORT =
  "category__sort_order,category__name,sort_order,name";

interface UseCheckoutAddonsOpts {
  productPageSize?: number;
  productSort?: string;
}

export default function useCheckoutAddons({
  productPageSize = 10,
  productSort = DEFAULT_PRODUCT_SORT,
}: UseCheckoutAddonsOpts = {}) {
  const { isFlagActive } = useDjangoWaffle();
  const basketService = usePublicBasket();
  const { isMembershipTicketExchange: isSeatSwapActive } =
    usePublicExchangeManager();

  const {
    paymentType,
    purchaseSourcePlatform,
    basketItemTypes,
    basketEventIds,
    basketMembershipIds,
    basketMembershipLevelIds,
    paymentVenue,
    isBasketUpdating,
    holdLinkSlug,
  } = basketService;

  // Capture initial basket event IDs on mount to exclude from upsell
  // This ensures events added during checkout remain visible in upsell
  const initialBasketEventIds = useRef(basketEventIds);

  const eventList = (basketEventIds || []).join(",");
  const ticketList = (basketItemTypes || []).join(",");

  // Only include membership params if the feature flag is enabled for this venue
  const isMembershipProductAddonsEnabled = isFlagActive(
    "show_membership_product_addons",
    paymentVenue
  );
  const membershipGroupList = isMembershipProductAddonsEnabled
    ? (basketMembershipIds || []).join(",")
    : "";
  const membershipLevelList = isMembershipProductAddonsEnabled
    ? (basketMembershipLevelIds || []).join(",")
    : "";
  const { usePaginated: usePaginatedProducts } = usePublicProductsService();
  const { useRetrieve, usePublicUpcomingEvents } = usePublicVenueService();

  const { data: venueDataForAddons, isSuccess: isVenueSuccess } =
    useRetrieve(paymentVenue);

  // Products are enabled if there are events OR memberships in the basket
  const isProductsEnabled =
    Boolean(eventList.length) || Boolean(membershipGroupList.length);

  /**
   * TODO: find solution hide upsell events if only memberships in the basket to prevent cannibalizing sales
   * initialBasketEventIds.current?.length > 0
   * There may be situations where we want to upsell specific events for specific memberships
   */
  const isEventUpsellEnabled =
    Boolean(venueDataForAddons?.slug) &&
    Boolean(venueDataForAddons?.enable_events_checkout) &&
    initialBasketEventIds.current?.length > 0;

  const customEvents = venueDataForAddons?.custom_events_checkout;

  const getUpsellQueryParams = useMemo(() => {
    const baseParams = {
      venue__in: paymentVenue,
      ordering: "starts_on", // this is a JSON field not a Django-style field
      exclude_events: initialBasketEventIds.current?.length
        ? initialBasketEventIds.current.join(",")
        : undefined,
    };

    if (customEvents?.length) {
      return {
        ...baseParams,
        event_ids: customEvents.join(","),
        page_size: customEvents.length,
      };
    }

    return {
      ...baseParams,
      page_size: 5,
      hide_children: true,
      hide_all_attractions: true,
    };
  }, [paymentVenue, customEvents]);

  const { upcomingEvents: upsellEventsList, isSuccess: isUpsellEventsSuccess } =
    usePublicUpcomingEvents(
      venueDataForAddons?.slug || "",
      getUpsellQueryParams,
      {
        enabled: isEventUpsellEnabled,
      }
    );

  // Use shared donation addon logic
  const { charityToDisplay, donationsEvent, isDonationsReady } =
    useDonationsAddon({
      dataProvider: DataProvider.Public,
    });

  const { upsellEvents } = useMemo(() => {
    if (!upsellEventsList) {
      return {
        upsellEvents: [],
      };
    }

    // Handle upsell events
    let filteredUpsellEvents: typeof upsellEventsList = [];
    if (isEventUpsellEnabled && upsellEventsList) {
      const eligibleEvents = upsellEventsList.filter(isEventEligibleForUpsell);

      if (customEvents?.length) {
        filteredUpsellEvents = eligibleEvents;
      } else {
        filteredUpsellEvents = eligibleEvents.slice(0, 5);
      }
    }

    return {
      upsellEvents: filteredUpsellEvents,
    };
  }, [upsellEventsList, customEvents, isEventUpsellEnabled]);

  const {
    data: productsData,
    fetchNextPage: fetchNextPageProducts,
    hasNextPage: hasNextPageProducts,
    isSuccess: isProductsSuccess,
    isLoading: isProductsLoading,
    isFetchingNextPage: isProductsFetchingNextPage,
  } = usePaginatedProducts({
    params: {
      // Event-based product filtering
      event: eventList || undefined,
      ticket: ticketList || undefined,
      // Membership-based product filtering
      membership_group: membershipGroupList || undefined,
      membership_level: membershipLevelList || undefined,
      page: 1,
      page_size: productPageSize,
      payment_type: paymentType,
      purchase_platform: purchaseSourcePlatform,
      ordering: productSort,
    },
    options: {
      enabled: isProductsEnabled,
    },
  });

  const products = useMemo(() => {
    if (productsData) {
      const flatten = getFlattenedData(productsData);
      const filter: ProductTypeSerializer[] = [];
      flatten.forEach((product) => {
        let hasInventory = false;
        let areAllSoldOut = true;
        if (product?.product_attributes && product?.product_attributes.length) {
          hasInventory = product?.product_attributes.some(
            (attribute) => (attribute?.inventory || 0) > 0
          );
          areAllSoldOut = product?.product_attributes.every(
            (attribute) => attribute.sold_out
          );
        }

        if (hasInventory && !areAllSoldOut) {
          filter.push(product);
        }
      });
      return filter;
    }
    return [];
  }, [productsData]);

  const addonsLoaded = useMemo(() => {
    const productsReady =
      !isProductsEnabled || (isProductsEnabled && isProductsSuccess);
    const upsellReady =
      !isEventUpsellEnabled || (isEventUpsellEnabled && isUpsellEventsSuccess);
    const venueReady =
      !paymentVenue || (Boolean(paymentVenue) && isVenueSuccess);

    return (
      productsReady &&
      upsellReady &&
      venueReady &&
      isDonationsReady &&
      !isBasketUpdating
    );
  }, [
    isProductsEnabled,
    isProductsSuccess,
    isEventUpsellEnabled,
    isUpsellEventsSuccess,
    paymentVenue,
    isVenueSuccess,
    isDonationsReady,
    isBasketUpdating,
  ]);

  return {
    products,
    upsellEvents,
    charityToDisplay,
    isBasketUpdating,
    isProductsLoading,
    isProductsFetchingNextPage,
    hasNextPageProducts,
    fetchNextPageProducts,
    donationsEvent,
    allInPricing: venueDataForAddons?.all_in_pricing_is_enabled,
    // Addons are eligible if there are events OR memberships in the basket (and not a hold link)
    addonsEligible:
      (Boolean(eventList.length) || Boolean(membershipGroupList.length)) &&
      !holdLinkSlug &&
      !isSeatSwapActive,
    addonsLoaded,
    // Show addons step only if there are actual products, upsell events, or donations
    hasAddOns: Boolean(
      products.length || upsellEvents.length || charityToDisplay
    ),
  };
}
